import classNames from 'classnames';
import React from "react";
import {v4 as uuidv4} from 'uuid';
import _ from 'lodash';

const {MDBSelect, MDBSelectInput, MDBSelectOptions, MDBSelectOption} = require("mdbreact");

/**
 * data list item could be compound value besides simple string and number.
 * It is basically a tree structure.
 */
export type DataListItem = string | number | boolean |{icon?:string}| {[key:string]:DataListItem}; 

interface DropdownOption {
    /** artificial uuid to identify the original value */
    value:string,
    text:string,
    origValue:DataListItem,
    icon?: string
}


interface DropDownListProps {
    // one of them must have value
    id?:string;
    name?: string;
    entity?:string;

    condition?: any; // never used
    label: string;
    className?: string;
    required?: boolean;
    readOnly?:boolean;
    disabled?:boolean;
    hint?: string;
    valueName: string;
    valueText: string;
    dataList: DataListItem[];

    error?:string;
    
    value:string|number;

    onChange: (value:DataListItem, entity:string|undefined, name?:string)=>void;
}

interface DropDownListState {
    isFocused: boolean, 
    dataList: DataListItem[],
    error: string,
    optionValues: DropdownOption[]
}


function getNestedData (dataValue: DataListItem, 
                       dataNameList: string | any[] | null): DataListItem {
    if (dataNameList != null && dataNameList.length >0) {
        // dataValue should be an object
        const dv = dataValue as {[key:string]:DataListItem};
        const nextDV = dv[dataNameList[0]];
        if(nextDV){
            return getNestedData(nextDV, dataNameList.slice(1, dataNameList.length));
        }else{
            console.error(`DropdownList:missing nested data value of ${dataNameList} from `, dataValue);
            throw Error(`DropdownList:missing nested data value of ${dataNameList} from ${JSON.stringify(dataValue)}`);
        }
    }
    else {
        // dataValue should be a simple value?
        return dataValue;
    }
}

/**
 * mdbreact does not handle empty string value or non string value such as number.
 * so we need to convert dataList into pure string value.
 *
 * @param {*} dataList
 */
function convertDataList (dataList:DataListItem[], valueName:string, valueText:string):DropdownOption[]{
    return (dataList||[]).map(
        (listValue,listIndex)=> {
            let valueNameList = valueName.split(".");
            let valueTextList = valueText.split(".");

            let listId = getNestedData(listValue, valueNameList);
            let listDescription = getNestedData(listValue, valueTextList) as string;
            if(typeof listDescription !== 'string'){
                console.error(`DropdownList: listDescription is not string`, listDescription);
                throw Error('DropdownList: listDescription is not string:'+listDescription);
            }
            let icon = (listValue as {icon?:string})['icon'];
            if(typeof icon !== 'string'){
                icon = undefined;
            }
            return {value: uuidv4(), text: listDescription, origValue: listId, icon}
        })
}

function calcId(props:DropDownListProps){
    let {name, entity, id} = props;
    if(!id){
        if(entity){
            id = entity+"-"+name
        }else{
            id = name;
        }
    }
    if(id){
        id = (id.charAt(0).toLowerCase() + id.slice(1)).replace(/\./g, "-"); // enforce the first character of id is lower case
        return id;
    }else{
        console.error(`DropdownList: missing name, entity, and id in props`, props);
        throw Error(`DropdownList: missing name, entity, and id in props`);
    }
}

/**
 * It remedies a number of mdbreact select component:
 * 1. it does not handle empty string value
 * 2. it does not handle non string value such as number
 * 3. it returns array of values when user selects a different value
 * 4. it does not render again if only MDBSelectOption checked attribute changed.
 */
class DropDownList extends React.Component<DropDownListProps,DropDownListState> {
    private id: string = uuidv4(); // default unique value

    constructor (props:DropDownListProps) {
        super(props);
        this.state = {isFocused: false, 
                      dataList: _.cloneDeep(props.dataList),
		              error: '',
                      optionValues: (convertDataList(props.dataList, props.valueName, props.valueText)||[])
        }
    }

    onChange = (v:string[])=> {
        if(v.length<1) return;

        let x = v[0];  // is it always array of length 1?
        let optValue = this.state.optionValues.find(v=>v.value === x);
        if(!optValue) return;

        let origValue = optValue.origValue;
        if(origValue !== this.props.value){
            // console.log(`DropdownList${this.uuid} ${this.props.label} onChangeHandler oldv=[${JSON.stringify(this.props.value)}] v=[${JSON.stringify(origValue)}] entity=${this.props.entity} name=${this.props.name}`);
            this.props.onChange(origValue, this.props.entity, this.props.name);
        }
    }

    static getDerivedStateFromProps(props:DropDownListProps, state:DropDownListState){
        if(_.isEqual(props.dataList, state.dataList)) return null;
        return { dataList: _.cloneDeep(props.dataList),
            optionValues: convertDataList(props.dataList, props.valueName, props.valueText)}
    }

    render() {
        const {
            label,
            readOnly,
            disabled,
          hint,
        } = this.props;
        // console.log(`DropdownList${this.uuid} ${this.props.label} render ${entity}/${name} ${value} options=${JSON.stringify(this.state.optionValues)}`+
        //             ` dataList=${JSON.stringify(dataList)}`);

        let condition = this.props.condition == undefined ? true : this.props.condition;
        let id = calcId(this.props);
        id = id.replace(/[\[\]]/g, "_")
        this.id = id;
        let wrapperClasses = (this.props.className ? this.props.className : '') +
            classNames(' ui-renderer-select-wrapper', {'has-focus': this.state.isFocused})
        let tabindex = readOnly ? -1 : 0;
		let hasError = !!this.props.error;
        
        return (
            <div id={id+'-wrapper'} className={wrapperClasses}>
                <MDBSelect id={id+'Select'} className={this.props.disabled? 'disabled ' : ''} getValue={this.onChange} multiple={false}>
                    <label htmlFor={id} className={'no-break active' + (this.props.disabled? ' disabled ' : '')} >{label}</label>
                    <MDBSelectInput selected={hint ? hint : undefined} disabled={disabled} readOnly={readOnly} tabIndex={tabindex}/>
                    <MDBSelectOptions  tabIndex={tabindex}> 
                        { condition &&
                        this.state.optionValues && this.state.optionValues.map(
                            (optValue,listIndex)=> {
                                let {value, text, origValue, icon} = optValue;
                                let checked = origValue===this.props.value;
                                
                                return <MDBSelectOption className={'selectItems'} key={`${value}-${checked}-${icon}`}
                                                        checked={checked} icon={icon} id={id+'-'+origValue}
                                                        value={value}>{text}</MDBSelectOption>
                            })
                        }
                        {
                            !condition &&
                            <MDBSelectOption className={"selectItems"} />
                        }
                    </MDBSelectOptions>
                </MDBSelect>
				<div className={"invalid-feedback"} style={{ display: hasError && !this.props.readOnly ? 'block' : 'none' }}>{this.props.error}</div>

            </div>
        );
    }

    setInputAttributes = ()=>{
        let inputElem = document.querySelector(`#${this.id}-wrapper.ui-renderer-select-wrapper input`);
        if(inputElem){
            inputElem.setAttribute('id', this.id);
        }else{
            // console.log(`can not find #${this.id}.employee-portal-select-wrapper  input`);
        }
    }

    componentDidMount(){
        this.setInputAttributes();
    }

    componentDidUpdate(){
        this.setInputAttributes();
    }
}


export default DropDownList;